import { SdkContext } from '../internal'

/** A group of members: department, area, squad, committee. */
export interface MemberGroup {
  id: string
  name: string
  description: string | null
  parentGroupId: string | null
  color: string | null
  order: number
  leader: { id: string; name: string | null; avatar: string | null } | null
  membersCount: number
  /** Everyone in the community belongs to this one, new arrivals included. */
  isDefault: boolean
}

/** One box of the org chart, carrying the leader it hangs from. */
export interface OrgChartNode {
  id: string
  name: string | null
  avatar: string | null
  role: string
  tagline: string | null
  reportsToId: string | null
  groups: Array<{ id: string; name: string; color: string | null; isPrimary: boolean }>
  directReportsCount: number
  /** True when this person has reports the answer did not include. */
  hasUnloadedReports: boolean
}

export interface OrgChart {
  nodes: OrgChartNode[]
  /** The answer hit its ceiling. It says so rather than cutting in silence. */
  truncated: boolean
  limit: number
  depth: number
}

export class MemberGroupsResource {
  constructor(private ctx: SdkContext) {}

  async list(): Promise<MemberGroup[]> {
    return this.ctx.call({ httpMethod: 'GET', path: '/member-groups' })
  }

  async create(data: Partial<MemberGroup> & { name: string }): Promise<MemberGroup> {
    return this.ctx.call({ httpMethod: 'POST', path: '/member-groups', hasBody: true }, { body: data })
  }

  async update(id: string, data: Partial<MemberGroup>): Promise<MemberGroup> {
    return this.ctx.call(
      { httpMethod: 'PUT', path: '/member-groups/{id}', hasBody: true },
      { id, body: data }
    )
  }

  async delete(id: string): Promise<void> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/member-groups/{id}' }, { id })
  }
}

/** `GET /hierarchy/me`: whether the person leads anyone, and who answers to them directly. */
export interface MyTeam {
  isLeader: boolean
  directReports: Array<{ memberId: string; name: string | null; avatarUrl: string | null }>
  hasIndirectReports: boolean
}

export class HierarchyResource {
  constructor(private ctx: SdkContext) {}

  /** Anyone signed in. `spaceId` keeps only the direct reports who open that space. */
  async me(spaceId?: string): Promise<MyTeam> {
    return this.ctx.call({ httpMethod: 'GET', path: '/hierarchy/me' }, { query: spaceId ? { spaceId } : {} })
  }

  /**
   * The chart, flat, each node carrying its `reportsToId`.
   *
   * Never ask for a whole community: pass a root or a group. The API stops at
   * its own ceiling anyway and answers `truncated`.
   */
  async orgChart(params: {
    rootMemberId?: string
    groupId?: string
    depth?: number
    limit?: number
  } = {}): Promise<OrgChart> {
    return this.ctx.call({ httpMethod: 'GET', path: '/hierarchy/org-chart' }, { query: params })
  }

  /**
   * Sets a person's leader, their groups, or both (owner and admin since F-167; 403 to the rest).
   *
   * `groupIds` replaces the whole set — leaving it out keeps what is there.
   * `groupExpirations` (F-167): per group the person is in, `YYYY-MM-DD` (valid through that day in the
   * community's zone) or `null` (permanent); a group left out keeps its date. A past date is 400 `past`.
   */
  async setMember(
    id: string,
    data: { reportsToId?: string | null; groupIds?: string[]; primaryGroupId?: string | null; groupExpirations?: Record<string, string | null> }
  ) {
    return this.ctx.call(
      { httpMethod: 'PUT', path: '/hierarchy/members/{id}', hasBody: true },
      { id, body: data }
    )
  }

  /** For the warning before removing somebody: how many move up a level. */
  async directReportsCount(id: string): Promise<{ id: string; directReportsCount: number }> {
    return this.ctx.call(
      { httpMethod: 'GET', path: '/hierarchy/members/{id}/direct-reports-count' },
      { id }
    )
  }
}
